import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import {
 Heading,
 Flex,
 Box,
 Button,
 Progress,
 Text,
 useToast,
 Tabs,
 TabList,
 Tab,
 TabPanels,
 TabPanel,
 IconButton,
 Tooltip,
} from '@chakra-ui/react';
import { IoCheckmarkCircle, IoCheckmarkCircleOutline } from 'react-icons/io5';
import { fetchSurveyData } from '../../services/fetch';
import baseService from '../../services/base';
import { useSSO } from '../../context/SSO';
import { AttachEvidenceModal } from '../../components/Modals';
import { QuestionCardSimple } from '../../components/Card';
import { downloadFile } from '../../helpers/downloadFile';
import { DownloadResponsesButton, EvidenceDownloadButton } from '../../components/Button';
import RespondentView from '../../components/Survey/RespondentView';
import { initializeDefaultValues, typeMapping } from './certifications/defaultValues';
import RespondentViewReplica from '../../components/Survey/RespondentViewReplica';
import { useResponsesDownload } from '../../hooks/useResponsesDownload';
import useAuditHeaderData, { useIsAudit } from '../audit/useAuditHeaderData';
import {
 respostaComConteudo,
 totalIndicadores,
 indicadoresRespondidos,
} from '../../helpers/medidasGri';

export default function Questionnaire({
 companyId,
 surveyId,
 companyGroupId,
 formName,
 selectedCompanyId,
 openChat,
 isResponsibleUser,
}) {
 const toast = useToast();
 const { user, hasMasterRole } = useSSO();
 const isMaster = hasMasterRole();
 const isAudit = useIsAudit();
 const { downloadResponses, isLoadingResponses: isDownloadingResponses } =
  useResponsesDownload();

 const [isEvidenceModalOpen, setEvidenceModalOpen] = useState(false);
 const [currentQuestionId, setCurrentQuestionId] = useState();
 const [currentFiles, setCurrentFiles] = useState([]);
 const [isLoadingEvidence, setLoadingEvidence] = useState(false);
 const [isLoadingResponses, setLoadingResponses] = useState(false);
 const [replicaData, setReplicaData] = useState([]);
 const [selectedIndex, setSelectedIndex] = useState(0);
 const [isViewMode, setIsViewMode] = useState(false);
 const [surveyStatus, setSurveyStatus] = useState(null);
 const [isTogglingStatus, setIsTogglingStatus] = useState(false);

 const { data } = useSWR(
  surveyId && selectedCompanyId
   ? `survey/respondent/company?surveyId=${surveyId}&id_company=${selectedCompanyId}`
   : null,
  fetchSurveyData
 );

 const getSurveyReplicas = useCallback(async () => {
  try {
   const replicaResponse = await baseService.get(
    `/survey/replica/${surveyId}?id_company=${companyId}`
   );
   const replicaIds = replicaResponse.data;

   if (replicaIds && replicaIds.length > 0) {
    const replicaResults = await Promise.all(
     replicaIds.map((replica) =>
      baseService.get(
       `/survey/respondent?id_company=${companyId}&surveyId=${replica.id_survey}&id_user=${user?.user?.id_user}`
      )
     )
    );

    const processedReplicaData = replicaResults
     .map((result) => result?.data[0])
     .filter(Boolean);

    return processedReplicaData;
   }
   return [];
  } catch (err) {
   throw new Error(`Erro ao recuperar réplicas: ${err.message}`);
  }
 }, [surveyId, companyId, user?.user?.id_user]);

 useEffect(() => {
  const fetchReplicas = async () => {
   if (data?.survey?.is_replicable) {
    try {
     const replicasData = await getSurveyReplicas();
     setReplicaData(replicasData);
    } catch (error) {
     console.error('Erro ao buscar réplicas:', error);
    }
   }
  };

  fetchReplicas();
 }, [data?.survey?.is_replicable, getSurveyReplicas]);

 useEffect(() => {
  if (data?.survey?.status !== undefined) {
   setSurveyStatus(data.survey.status);
  }
 }, [data?.survey?.status, surveyId]);

 const toggleSurveyStatus = async () => {
  if (!isMaster) return;
  const newStatus = !surveyStatus;
  setIsTogglingStatus(true);
  try {
   await baseService.patch(`/survey/${surveyId}/status`, { status: newStatus });
   setSurveyStatus(newStatus);
   toast({
    title: newStatus ? 'Questionário ativado' : 'Questionário desativado',
    status: 'success',
   });
  } catch {
   toast({ title: 'Erro ao alterar status', status: 'error' });
  } finally {
   setIsTogglingStatus(false);
  }
 };

 const getCurrentQuestions = useCallback(
  (index) => {
   if (index === 0) {
    return (
     data?.survey?.questions?.filter(
      (question) => question?.company?.id_company === +selectedCompanyId
     ) || []
    );
   } else {
    const replicaIndex = index - 1;
    return replicaData[replicaIndex]?.survey?.questions || [];
   }
  },
  [data?.survey?.questions, replicaData, selectedCompanyId]
 );

 const currentCompanyQuestions = getCurrentQuestions(selectedIndex);

 const totalQuestions = currentCompanyQuestions?.length || 0;

 // GRI-07: linha fantasma (question_response de value vazio, sem arquivo e sem
 // medida) não conta como respondida — senão o número sobe por indicador.
 const questaoRespondida = useCallback(
  (question) => {
   if (!respostaComConteudo(question, selectedCompanyId)) return false;

   if (question.has_reviewer) {
    if (question.user_responses) {
     return question.user_responses.reviewer_approved === true;
    }
    if (Array.isArray(question.users_responses)) {
     return question.users_responses.some(
      (response) =>
       response?.company?.id_company === +selectedCompanyId &&
       response.reviewer_approved === true
     );
    }
    return false;
   }

   return true;
  },
  [selectedCompanyId]
 );

 const getAnsweredQuestionsCount = useCallback(
  (questions) => (questions ?? []).filter(questaoRespondida).length,
  [questaoRespondida]
 );

 const answeredQuestions = useMemo(() => {
  return getAnsweredQuestionsCount(currentCompanyQuestions);
 }, [currentCompanyQuestions, getAnsweredQuestionsCount]);

 const { requiredQuestionsCount, answeredRequiredQuestionsCount } = useMemo(() => {
  if (!currentCompanyQuestions?.length) {
   return { requiredQuestionsCount: 0, answeredRequiredQuestionsCount: 0 };
  }

  return {
   requiredQuestionsCount: currentCompanyQuestions.filter((q) => q.is_required).length,
   answeredRequiredQuestionsCount: currentCompanyQuestions.filter(
    (q) => q.is_required && questaoRespondida(q)
   ).length,
  };
 }, [currentCompanyQuestions, questaoRespondida]);

 const totalIndicators = useMemo(
  () => totalIndicadores(currentCompanyQuestions),
  [currentCompanyQuestions]
 );

 const answeredIndicators = useMemo(
  () => indicadoresRespondidos(currentCompanyQuestions, selectedCompanyId),
  [currentCompanyQuestions, selectedCompanyId]
 );

 const hasEvidenceFiles = useMemo(() => {
  return currentCompanyQuestions?.some((question) => {

   if (question.user_responses) {
    return question.user_responses.response_file?.length > 0;
   }

   return question?.users_responses?.some(
    (response) =>
     response?.company?.id_company === +selectedCompanyId &&
     response?.response_file?.length > 0
   );
  });
 }, [currentCompanyQuestions, selectedCompanyId]);

const defaultValues = useMemo(() => {

  const mainDefaultValues = currentCompanyQuestions.length ? {
    [surveyId]: initializeDefaultValues(currentCompanyQuestions, surveyId)
  } : {};

  const replicaDefaultValues = replicaData.reduce((acc, replicaItem) => {
    if (replicaItem?.survey?.questions?.length) {
      const replicaSurveyId = replicaItem.survey.id_survey;
      const questions = replicaItem.survey.questions;

      acc[replicaSurveyId] = questions.reduce((questionAcc, question) => {
        const { type, id_question, user_responses, options } = question;
        const questionName = `${typeMapping[type] || ''}_question_${id_question}`;

        let initialValue = {
          value: type === 'multiple_choice' ? [] : '',
          files: [],
          idResponse: null
        };

        if (user_responses?.response) {
          if (type === 'multiple_choice' || type === 'multi_choice') {
            let values = [];
            
            if (type === 'multiple_choice') {
              values = user_responses.response.includes('||') 
                ? user_responses.response.split('||').map(v => v.trim())
                : user_responses.response.includes(',') 
                  ? user_responses.response.split(',').map(v => v.trim())
                  : [user_responses.response.trim()];
            } else {
              values = [user_responses.response];
            }

            initialValue = {
              value: type === 'multi_choice' ? values[0] || '' : values,
              files: user_responses.response_file || [],
              idResponse: user_responses.id_question_response
            };
          } else {
            initialValue = {
              value: user_responses.response,
              files: user_responses.response_file || [],
              idResponse: user_responses.id_question_response
            };
          }
        }

        questionAcc[questionName] = initialValue;
        return questionAcc;
      }, {});
    }
    return acc;
  }, {});
  return {
    ...mainDefaultValues,
    ...replicaDefaultValues
  };
}, [currentCompanyQuestions, replicaData, surveyId]);

 async function evidenceDownload() {
  setLoadingEvidence(true);
  try {
   const response = await baseService.get(
    `/downloads?survey=${surveyId}&group=${companyGroupId}&form=${formName}`,
    { responseType: 'arraybuffer' }
   );

   if (response.headers['content-type'].includes('application/json')) {
    const data = JSON.parse(new TextDecoder().decode(response.data));
    downloadFile(data.url, `${formName}_evidence.zip`);
   } else {
    const blob = new Blob([response.data], { type: 'application/zip' });
    downloadFile(window.URL.createObjectURL(blob), `${formName}_evidence.zip`);
   }
   toast({
    title: 'Download das evidências iniciado com sucesso',
    status: 'success',
   });
  } catch (error) {
   toast({
    title: 'Erro ao fazer o download das evidências',
    description: 'Não foi possível processar os arquivos anexados. Tente novamente.',
    status: 'error',
   });
  } finally {
   setLoadingEvidence(false);
  }
 }

 const handleQuestionEvidence = useCallback((questionId, response) => {
  setCurrentQuestionId(questionId);
  setCurrentFiles(response?.response_file);
  setEvidenceModalOpen(true);
 }, []);

 // EG-63: em auditoria o sub-header some; o AuditHeader único mostra o progresso.
 useAuditHeaderData(
  isAudit
   ? {
      answered: answeredQuestions,
      total: totalQuestions,
      requiredAnswered: answeredRequiredQuestionsCount,
      requiredTotal: requiredQuestionsCount,
      indicatorsAnswered: answeredIndicators,
      indicatorsTotal: totalIndicators,
      downloadingResponses: isDownloadingResponses,
      downloadingEvidence: isLoadingEvidence,
      evidenceDisabled: !hasEvidenceFiles,
      onViewAsRespondent: () => setIsViewMode(true),
      onDownloadResponses: () =>
       downloadResponses({ surveyId, companyId: selectedCompanyId, companyGroupId, formName }),
      onDownloadEvidence: () => evidenceDownload(),
     }
   : null
 );

 const handleCloseCurrentEvidence = () => {
  setEvidenceModalOpen(false);
  setCurrentQuestionId();
  setCurrentFiles();
 };

 const QuestionsContent = ({ questions }) => (
  <Box>
   {questions?.map((question, index) => (
    <QuestionCardSimple
     key={`${question?.id_question}_${index}`}
     question={question}
     index={index}
     selectedCompanyId={selectedCompanyId}
     handleQuestionEvidence={handleQuestionEvidence}
     surveyId={surveyId}
     userId={user?.user?.id_user}
     questionnaireType='simple'
     openChat={openChat && openChat.questionId === question.id_question}
     isResponsibleUser={isResponsibleUser}
     mt='0'
    />
   ))}
  </Box>
 );

 const ProgressSection = () => (
  <Box
   bg='white'
   p={data?.survey?.is_replicable ? '6' : '4'}
   borderRadius={data?.survey?.is_replicable ? '0' : '12px'}
   mb={data?.survey?.is_replicable ? '0' : '6'}
  >
   <Flex gap='5' overflowY='hidden' pos='absolute' right='32px' top='70px'>
    <DownloadResponsesButton
     surveyId={surveyId}
     companyId={selectedCompanyId}
     companyGroupId={companyGroupId}
     formName={formName}
     isLoadingResponses={isLoadingResponses}
     setLoadingResponses={setLoadingResponses}
     height='51px'
    />
    <EvidenceDownloadButton
     isLoading={isLoadingEvidence}
     isDisabled={!hasEvidenceFiles}
     onClick={evidenceDownload}
    />
   </Flex>
   <Flex h='51px' align='center' justify='space-between' gap='6'>
    <Heading as='h3' fontSize='md'>
     Questões
    </Heading>

    <Flex align='center' gap='6'>

    {!isResponsibleUser && (
      <Button
       variant='underline'
       fontWeight={600}
       ml='auto'
       onClick={() => setIsViewMode(true)}
      >
       Visualizar como respondente
      </Button>
     )}

     <Flex
      h='51px'
      align='center'
      gap='14px'
      px='20px'
      bg='rgba(0, 115, 187, 0.1)'
      borderRadius='12px'
     >
      <Text as='span' fontSize='sm'>
       Respondidas
      </Text>

      <Box height='10px' w='80px'>
       <Progress
        value={(answeredQuestions / totalQuestions) * 100}
        height='10px'
        borderRadius='21px'
        bg='#E5E5E5'
       />
      </Box>
      <Text
       as='span'
       fontSize='sm'
       bg='white'
       fontWeight={600}
       p='8px 10px'
       h='35px'
       borderRadius='4px'
      >
       {answeredQuestions < 10 && '0'}
       {answeredQuestions || 0}/{totalQuestions < 10 && '0'}
       {totalQuestions || 0}
      </Text>
     </Flex>

     <Flex
      h='51px'
      align='center'
      gap='14px'
      px='20px'
      bg='rgba(234, 88, 43, 0.1)'
      borderRadius='12px'
     >
      <Text as='span' fontSize='sm'>
       Obrigatórias
      </Text>

      <Box height='10px' w='80px'>
       <Progress
        value={(answeredRequiredQuestionsCount / requiredQuestionsCount) * 100}
        height='10px'
        borderRadius='21px'
        bg='#E5E5E5'
        colorScheme='orange'
       />
      </Box>
      <Text
       as='span'
       fontSize='sm'
       bg='white'
       fontWeight={600}
       p='8px 10px'
       h='35px'
       borderRadius='4px'
      >
       {answeredRequiredQuestionsCount < 10 && '0'}
       {answeredRequiredQuestionsCount}/ {requiredQuestionsCount < 10 && '0'}
       {requiredQuestionsCount}
      </Text>
     </Flex>

     {/* GRI-07: indicador é medida da questão, não questão — contagem à parte. */}
     {totalIndicators > 0 && (
      <Flex
       h='51px'
       align='center'
       gap='14px'
       px='20px'
       bg='rgba(27, 94, 48, 0.1)'
       borderRadius='12px'
      >
       <Text as='span' fontSize='sm'>
        Indicadores
       </Text>

       <Box height='10px' w='80px'>
        <Progress
         value={(answeredIndicators / totalIndicators) * 100}
         height='10px'
         borderRadius='21px'
         bg='#E5E5E5'
         colorScheme='green'
        />
       </Box>
       <Text
        as='span'
        fontSize='sm'
        bg='white'
        fontWeight={600}
        p='8px 10px'
        h='35px'
        borderRadius='4px'
       >
        {answeredIndicators}/{totalIndicators}
       </Text>
      </Flex>
     )}

     {isMaster && (
      <Tooltip label={surveyStatus ? 'Desativar questionário' : 'Ativar questionário'}>
       <IconButton
        aria-label='Alternar status do questionário'
        icon={surveyStatus
         ? <IoCheckmarkCircle size={22} color='#38A169' />
         : <IoCheckmarkCircleOutline size={22} color='#A0AEC0' />
        }
        variant='ghost'
        size='sm'
        isLoading={isTogglingStatus}
        onClick={toggleSurveyStatus}
       />
      </Tooltip>
     )}
    </Flex>
   </Flex>
  </Box>
 );

 if (!data || !currentCompanyQuestions?.length) {
  return null;
 }
 
 if (isViewMode) {
  return (
   <RespondentViewReplica
    defaultValues={defaultValues}
    surveyId={surveyId}
    companyId={selectedCompanyId}
    userId={user?.user?.id_user}
    questions={currentCompanyQuestions}
    companyName={currentCompanyQuestions[0]?.company?.name}
    description={formName}
    totalAnsweredQuestions={answeredQuestions}
    requiredQuestionsCount={requiredQuestionsCount} 
    answeredRequiredQuestionsCount={answeredRequiredQuestionsCount} 
    onClose={() => setIsViewMode(false)}
    replicaData={replicaData}
    isReplicable={data?.survey?.is_replicable} 
    />
  );
 }

 return (
  <Box>
   {!data?.survey?.is_replicable && !isAudit && <ProgressSection />}

   <Box>
    {data?.survey?.is_replicable ? (
     <Tabs index={selectedIndex} onChange={setSelectedIndex} pos='static' mt='6'>
      <TabList
       gap='8px'
       border='none'
       overflowX='auto'
       overflowY='hidden'
       css={{
        '&::-webkit-scrollbar': { display: 'none' },
        scrollbarWidth: 'none',
       }}
      >
       <Tab {...tabStyles(0 === selectedIndex)} borderTopRadius='8px'>
        Principal
       </Tab>
       {replicaData.map((replica, index) => (
        <Tab key={replica.survey?.id_survey} {...tabStyles(index + 1 === selectedIndex)}>
         Replicado {index + 1}
        </Tab>
       ))}
      </TabList>

      {!isAudit && <ProgressSection />}

      <TabPanels bg='white'>
       <TabPanel pt='0'>
        <QuestionsContent questions={currentCompanyQuestions} />
       </TabPanel>

       {replicaData.map((_, index) => (
        <TabPanel key={index} pt='0'>
         <QuestionsContent questions={getCurrentQuestions(index + 1)} />
        </TabPanel>
       ))}
      </TabPanels>
     </Tabs>
    ) : (
     <QuestionsContent questions={currentCompanyQuestions} />
    )}
   </Box>

   {currentQuestionId && (
    <AttachEvidenceModal
     isOpen={isEvidenceModalOpen}
     onClose={handleCloseCurrentEvidence}
     questionId={currentQuestionId}
     files={currentFiles}
    />
   )}
  </Box>
 );
}

const tabStyles = (isSelected) => ({
 color: isSelected ? '#292B30' : '#ADAFB3',
 bg: '#FEFEFE',
 minW: '123px',
 height: '46px',
 fontSize: '18px',
 fontWeight: 600,
 border: 'none',
 borderRadius: '8px 8px 0px 0px',
 alignItems: 'center',
 p: '14px 24px',
 _hover: { color: '#5C6066', bg: 'rgba(92, 96, 102, 0.1)' },
});
